import apiClient, { handleApiError } from '../../utils/apiClient';
import type { DoctorShift, DoctorShiftListResponse } from '../../types/doctorShift';

/**
 * APIレスポンス型定義
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * シフト一覧取得パラメータ（snake_case for API）
 */
interface GetShiftsApiParams {
  start_date: string;
  end_date: string;
  doctor_id?: string;
  start_time_from?: string;
  start_time_to?: string;
  page?: number;
  page_size?: number;
}

/**
 * シフト作成リクエスト（snake_case for API）
 */
interface CreateShiftApiRequest {
  doctor_id: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  slot_type?: 'normal' | 'urgent';
}

/**
 * シフト更新リクエスト（snake_case for API）
 */
interface UpdateShiftApiRequest {
  doctor_id?: string;
  shift_date?: string;
  start_time?: string;
  end_time?: string;
  slot_type?: 'normal' | 'urgent';
  version?: number;
  force?: boolean;
}


/**
 * datetime文字列から時間部分（HH:mm）を抽出する
 * @param datetime "YYYY/MM/DD HH:mm:ss" または "HH:mm" 形式
 * @returns "HH:mm" 形式の時間文字列
 */
const extractTimeFromDatetime = (datetime: string): string => {
  // すでにHH:mm形式の場合はそのまま返す
  if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(datetime)) {
    return datetime;
  }
  // "YYYY/MM/DD HH:mm:ss" または "YYYY-MM-DD HH:mm:ss" 形式から時間を抽出
  const match = datetime.match(/(\d{2}):(\d{2})(?::\d{2})?$/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }
  return datetime;
};

/**
 * 医師シフトAPI（バックエンドAPIとの通信を担当）
 */
export const doctorShiftApi = {
  /**
   * シフト一覧取得（全ページ取得）
   * @param params 取得パラメータ
   * @returns Promise<DoctorShift[]> シフト一覧
   */
  getShifts: async (params: {
    startDate: string;
    endDate: string;
    doctorId?: string;
    startTimeFrom?: string;
    startTimeTo?: string;
  }): Promise<DoctorShift[]> => {
    try {
      const allShifts: DoctorShift[] = [];
      let currentPage = 1;
      let hasNextPage = true;

      // 全ページを順次取得
      while (hasNextPage) {
        const apiParams: GetShiftsApiParams = {
          start_date: params.startDate,
          end_date: params.endDate,
          doctor_id: params.doctorId,
          start_time_from: params.startTimeFrom,
          start_time_to: params.startTimeTo,
          page: currentPage,
          page_size: 100, // 最大値
        };

        // 未定義のパラメータを除去
        const cleanParams = Object.fromEntries(
          Object.entries(apiParams).filter(([, v]) => v !== undefined)
        );

        const response = await apiClient.get<ApiResponse<DoctorShiftListResponse>>(
          '/api/doctor-shifts',
          { params: cleanParams }
        );

        if (!response.data.success || !response.data.data) {
          throw new Error(
            response.data.error?.message || '医師シフトの取得に失敗しました'
          );
        }

        // start_time, end_timeをHH:mm形式に変換して追加
        const transformedShifts = response.data.data.data.map((shift) => ({
          ...shift,
          start_time: extractTimeFromDatetime(shift.start_time),
          end_time: extractTimeFromDatetime(shift.end_time),
        }));
        allShifts.push(...transformedShifts);

        // 次ページがあるか確認
        hasNextPage = response.data.data.meta?.has_next_page ?? false;
        currentPage++;
      }

      return allShifts;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * シフト新規作成
   * @param data 作成データ
   * @returns Promise<DoctorShift> 作成されたシフト
   */
  createShift: async (data: {
    doctorId: string;
    shiftDate: string;
    startTime: string;
    endTime: string;
    slotType?: 'normal' | 'urgent';
  }): Promise<DoctorShift> => {
    try {
      const request: CreateShiftApiRequest = {
        doctor_id: data.doctorId,
        shift_date: data.shiftDate,
        start_time: data.startTime,
        end_time: data.endTime,
        slot_type: data.slotType,
      };

      const response = await apiClient.post<ApiResponse<DoctorShift>>(
        '/api/doctor-shifts',
        request
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error?.message || '医師シフトの作成に失敗しました'
        );
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * シフト更新
   * @param id シフトID
   * @param data 更新データ
   * @returns Promise<DoctorShift> 更新されたシフト
   */
  updateShift: async (
    id: string,
    data: {
      doctorId?: string;
      shiftDate?: string;
      startTime?: string;
      endTime?: string;
      slotType?: 'normal' | 'urgent';
      version?: number;
    }
  ): Promise<DoctorShift> => {
    try {
      const request: UpdateShiftApiRequest = {
        doctor_id: data.doctorId,
        shift_date: data.shiftDate,
        start_time: data.startTime,
        end_time: data.endTime,
        slot_type: data.slotType,
        version: data.version,
        force: true,
      };

      // 未定義のフィールドを除去
      const cleanRequest = Object.fromEntries(
        Object.entries(request).filter(([, v]) => v !== undefined)
      );

      const response = await apiClient.put<ApiResponse<DoctorShift>>(
        `/api/shift/doctor-shifts/${id}`,
        cleanRequest
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error?.message || '医師シフトの更新に失敗しました'
        );
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * シフト削除
   * @param id シフトID
   * @param version バージョン（楽観的ロック用）
   * @returns Promise<void>
   */
  deleteShift: async (id: string, version?: number): Promise<void> => {
    try {
      const params = version !== undefined ? { version } : {};

      const response = await apiClient.delete<ApiResponse<void>>(
        `/api/doctor-shifts/${id}`,
        { params }
      );

      if (!response.data.success) {
        throw new Error(
          response.data.error?.message || '医師シフトの削除に失敗しました'
        );
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },
};

export default doctorShiftApi;
